import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, MapPin, Clock, Users, Lock, 
  Download, Share2, Heart, Image, BookOpen,
  ChevronRight, ExternalLink, Check
} from 'lucide-react';

const API = '';

export default function SharedView() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [rsvpForm, setRsvpForm] = useState({ name: '', email: '', response: 'attending', guests: 0 });
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);

  useEffect(() => {
    fetchSharedResource();
  }, [shareToken]);

  const fetchSharedResource = async (pwd = null) => {
    try {
      setLoading(true);
      const url = pwd 
        ? `${API}/api/share/${shareToken}?password=${encodeURIComponent(pwd)}`
        : `${API}/api/share/${shareToken}`;
      
      const res = await axios.get(url);
      
      if (res.data.requires_password) {
        setNeedsPassword(true);
        setData(res.data);
      } else {
        setNeedsPassword(false);
        setData(res.data);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Incorrect password');
      } else if (err.response?.status === 410) {
        setError(err.response.data.detail || 'This link has expired');
      } else if (err.response?.status === 404) {
        setError('Share link not found');
      } else {
        setError('Failed to load shared content');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setError(null);
    fetchSharedResource(password);
  };

  const handleRsvp = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/share/${shareToken}/rsvp`, null, {
        params: rsvpForm
      });
      setRsvpSubmitted(true);
    } catch (err) {
      alert('Failed to submit RSVP');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error && !needsPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-xl p-8 max-w-md w-full">
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold text-white text-center mb-2">Password Protected</h2>
          <p className="text-gray-400 text-center mb-6">
            This {data?.resource_type} from <span className="text-white">{data?.family_name}</span> requires a password.
          </p>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            />
            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
            <button
              type="submit"
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Unlock Content
            </button>
          </form>
        </div>
      </div>
    );
  }

  const { share_info, resource } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-blue-200 text-sm mb-2">Shared by {share_info.shared_by}</p>
          <h1 className="text-3xl font-bold text-white">{share_info.resource_title}</h1>
          <p className="text-blue-200 mt-1">From {share_info.family_name}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Event View */}
        {share_info.resource_type === 'event' && (
          <div className="bg-slate-800 rounded-xl overflow-hidden">
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">{resource.name}</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-gray-300">
                      <Calendar className="w-5 h-5 text-blue-400" />
                      <span>{new Date(resource.start_date).toLocaleDateString('en-US', { 
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                      })}</span>
                    </div>
                    
                    {resource.start_time && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <Clock className="w-5 h-5 text-blue-400" />
                        <span>{resource.start_time}{resource.end_time && ` - ${resource.end_time}`}</span>
                      </div>
                    )}
                    
                    {resource.location && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <MapPin className="w-5 h-5 text-blue-400" />
                        <span>{resource.location}</span>
                      </div>
                    )}
                  </div>
                  
                  {resource.description && (
                    <p className="mt-6 text-gray-400">{resource.description}</p>
                  )}
                </div>

                {/* RSVP Form */}
                {share_info.permission === 'rsvp' && !rsvpSubmitted && (
                  <div className="bg-slate-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">RSVP to this Event</h3>
                    <form onSubmit={handleRsvp} className="space-y-4">
                      <input
                        type="text"
                        placeholder="Your Name"
                        value={rsvpForm.name}
                        onChange={(e) => setRsvpForm({ ...rsvpForm, name: e.target.value })}
                        required
                        className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                      />
                      <input
                        type="email"
                        placeholder="Your Email"
                        value={rsvpForm.email}
                        onChange={(e) => setRsvpForm({ ...rsvpForm, email: e.target.value })}
                        required
                        className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                      />
                      <select
                        value={rsvpForm.response}
                        onChange={(e) => setRsvpForm({ ...rsvpForm, response: e.target.value })}
                        className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                      >
                        <option value="attending">Yes, I'll be there!</option>
                        <option value="maybe">Maybe</option>
                        <option value="not_attending">Can't make it</option>
                      </select>
                      <input
                        type="number"
                        min="0"
                        placeholder="Additional Guests"
                        value={rsvpForm.guests}
                        onChange={(e) => setRsvpForm({ ...rsvpForm, guests: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                      />
                      <button
                        type="submit"
                        className="w-full py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Submit RSVP
                      </button>
                    </form>
                  </div>
                )}

                {rsvpSubmitted && (
                  <div className="bg-green-500/20 border border-green-500 rounded-xl p-6 text-center">
                    <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-white">RSVP Submitted!</h3>
                    <p className="text-green-300 mt-2">Thank you for responding.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Photo View */}
        {share_info.resource_type === 'photo' && (
          <div className="bg-slate-800 rounded-xl overflow-hidden">
            <img 
              src={`${API}${resource.file_url}`} 
              alt={resource.caption || 'Shared photo'} 
              className="w-full max-h-[70vh] object-contain bg-black"
            />
            <div className="p-6">
              {resource.caption && (
                <p className="text-white text-lg">{resource.caption}</p>
              )}
              <p className="text-gray-400 mt-2">
                Uploaded by {resource.uploader_name}
              </p>
              {resource.can_download && (
                <a 
                  href={`${API}${resource.file_url}`}
                  download
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              )}
            </div>
          </div>
        )}

        {/* Album View */}
        {share_info.resource_type === 'album' && (
          <div className="bg-slate-800 rounded-xl p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">{resource.name}</h2>
                {resource.description && (
                  <p className="text-gray-400 mt-2">{resource.description}</p>
                )}
              </div>
              {resource.can_download && resource.photos?.length > 0 && (
                <button
                  onClick={() => {
                    // Download all photos
                    resource.photos.forEach((photo, idx) => {
                      setTimeout(() => {
                        const link = document.createElement('a');
                        link.href = `${API}${photo.file_url}`;
                        link.download = `${resource.name}_${idx + 1}.jpg`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }, idx * 500);
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4" />
                  Download All ({resource.photos.length})
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {resource.photos?.map((photo) => (
                <div key={photo.photo_id} className="group relative aspect-square bg-slate-700 rounded-lg overflow-hidden">
                  <img 
                    src={`${API}${photo.file_url}`} 
                    alt={photo.caption || ''} 
                    className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                  />
                  {resource.can_download && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const link = document.createElement('a');
                        link.href = `${API}${photo.file_url}`;
                        link.download = `photo_${photo.photo_id}.jpg`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1.5 sm:p-2 bg-blue-600 text-white rounded-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-blue-700 text-xs sm:text-sm"
                      title="Download photo"
                    >
                      <Download className="w-4 h-4" />
                      <span className="sm:hidden">Download</span>
                    </button>
                  )}
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-sm truncate">{photo.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {resource.photos?.length === 0 && (
              <p className="text-gray-400 text-center py-8">No photos in this album</p>
            )}
            {!resource.can_download && resource.photos?.length > 0 && (
              <p className="text-gray-500 text-center mt-4 text-sm">
                Downloads are not enabled for this shared album
              </p>
            )}
          </div>
        )}

        {/* Story View */}
        {share_info.resource_type === 'story' && (
          <div className="bg-slate-800 rounded-xl p-8">
            <div className="flex items-center gap-2 text-blue-400 text-sm mb-4">
              <BookOpen className="w-4 h-4" />
              <span>{resource.category}</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">{resource.title}</h2>
            <p className="text-gray-400 mb-6">
              Shared by {resource.author_name} • {new Date(resource.created_at).toLocaleDateString()}
            </p>
            <div className="prose prose-invert max-w-none">
              <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{resource.content}</p>
            </div>
            {resource.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {resource.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-slate-700 text-gray-300 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Timeline View */}
        {share_info.resource_type === 'timeline' && (
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6">{share_info.family_name} Timeline</h2>
            <div className="space-y-4">
              {resource.events?.map((event) => (
                <div key={event.event_id} className="flex items-start gap-4 p-4 bg-slate-700 rounded-lg">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{event.name}</p>
                    <p className="text-gray-400 text-sm">{new Date(event.start_date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
              {resource.stories?.map((story) => (
                <div key={story.story_id} className="flex items-start gap-4 p-4 bg-slate-700 rounded-lg">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{story.title}</p>
                    <p className="text-gray-400 text-sm">{story.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            Shared via Legacy Forge Family - Building Family Legacies Together
          </p>
        </div>
      </div>
    </div>
  );
}
